import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle, Color } from '@tiptap/extension-text-style';
import { useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
  BoldIcon,
  ItalicIcon,
  ListBulletIcon,
  LinkIcon,
  PhotoIcon,
  CodeBracketIcon,
} from '@heroicons/react/24/outline';

interface TipTapEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
  isHtmlMode?: boolean;
  onHtmlModeChange?: (isHtmlMode: boolean) => void;
  className?: string;
}

export interface TipTapEditorRef {
  insertVariable: (variableKey: string) => void;
  getHTML: () => string;
  editor: Editor | null;
}

const MenuButton = ({
  onClick,
  isActive = false,
  disabled = false,
  title,
  children,
}: {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`p-2 rounded hover:bg-gray-200 transition-colors ${
      isActive ? 'bg-gray-200 text-brand' : 'text-gray-600'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    {children}
  </button>
);

const TipTapEditor = forwardRef<TipTapEditorRef, TipTapEditorProps>(
  ({ content, onChange, placeholder, isHtmlMode = false, onHtmlModeChange, className }, ref) => {
    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: {
            levels: [1, 2, 3, 4],
          },
        }),
        Link.configure({
          openOnClick: false,
          HTMLAttributes: {
            class: 'text-blue-600 underline',
          },
        }),
        Image.configure({
          HTMLAttributes: {
            class: 'max-w-full rounded',
          },
        }),
        TextAlign.configure({
          types: ['heading', 'paragraph'],
        }),
        TextStyle,
        Color,
      ],
      content,
      onUpdate: ({ editor }) => {
        onChange(editor.getHTML());
      },
      editorProps: {
        attributes: {
          class: 'prose prose-sm max-w-none focus:outline-none min-h-[300px] p-4',
        },
      },
    });

    // Sync content when it changes externally
    useEffect(() => {
      if (editor && content !== editor.getHTML()) {
        editor.commands.setContent(content, false);
      }
    }, [content, editor]);

    // Insert variable at cursor position
    const insertVariable = useCallback(
      (variableKey: string) => {
        if (editor) {
          editor.chain().focus().insertContent(`{{${variableKey}}}`).run();
        }
      },
      [editor]
    );

    // Expose methods to parent
    useImperativeHandle(ref, () => ({
      insertVariable,
      getHTML: () => editor?.getHTML() || '',
      editor,
    }));

    const setLink = useCallback(() => {
      if (!editor) return;

      const previousUrl = editor.getAttributes('link').href;
      const url = window.prompt('Enter URL:', previousUrl);

      if (url === null) return;

      if (url === '') {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
        return;
      }

      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }, [editor]);

    const addImage = useCallback(() => {
      if (!editor) return;

      const url = window.prompt('Enter image URL:');
      if (url) {
        editor.chain().focus().setImage({ src: url }).run();
      }
    }, [editor]);

    if (!editor) {
      return (
        <div className="border border-gray-300 rounded-lg p-4 min-h-[400px] flex items-center justify-center">
          <span className="text-gray-400">Loading editor...</span>
        </div>
      );
    }

    // HTML Mode - show textarea
    if (isHtmlMode) {
      return (
        <div className={`border border-gray-300 rounded-lg overflow-hidden ${className}`}>
          <div className="bg-gray-50 border-b border-gray-300 px-4 py-2 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">HTML Source</span>
            <button
              type="button"
              onClick={() => onHtmlModeChange?.(false)}
              className="text-sm text-brand hover:text-brand-dark"
            >
              Switch to Visual Editor
            </button>
          </div>
          <textarea
            value={content}
            onChange={(e) => onChange(e.target.value)}
            className="w-full min-h-[400px] p-4 font-mono text-sm focus:outline-none resize-y"
            placeholder={placeholder || 'Enter HTML content...'}
            aria-label="HTML source"
          />
        </div>
      );
    }

    // Visual Editor Mode
    return (
      <div className={`border border-gray-300 rounded-lg overflow-hidden ${className}`}>
        {/* Toolbar */}
        <div className="bg-gray-50 border-b border-gray-300 px-2 py-1 flex items-center gap-1 flex-wrap">
          {/* Text formatting */}
          <MenuButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            isActive={editor.isActive('bold')}
            title="Bold"
          >
            <BoldIcon className="w-4 h-4" />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            isActive={editor.isActive('italic')}
            title="Italic"
          >
            <ItalicIcon className="w-4 h-4" />
          </MenuButton>

          <div className="w-px h-6 bg-gray-300 mx-1" />

          {/* Headings */}
          <select
            value={
              editor.isActive('heading', { level: 1 })
                ? 'h1'
                : editor.isActive('heading', { level: 2 })
                ? 'h2'
                : editor.isActive('heading', { level: 3 })
                ? 'h3'
                : 'p'
            }
            onChange={(e) => {
              const value = e.target.value;
              if (value === 'p') {
                editor.chain().focus().setParagraph().run();
              } else {
                const level = parseInt(value.replace('h', '')) as 1 | 2 | 3;
                editor.chain().focus().toggleHeading({ level }).run();
              }
            }}
            className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-brand"
          >
            <option value="p">Paragraph</option>
            <option value="h1">Heading 1</option>
            <option value="h2">Heading 2</option>
            <option value="h3">Heading 3</option>
          </select>

          <div className="w-px h-6 bg-gray-300 mx-1" />

          {/* Lists */}
          <MenuButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editor.isActive('bulletList')}
            title="Bullet List"
          >
            <ListBulletIcon className="w-4 h-4" />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editor.isActive('orderedList')}
            title="Numbered List"
          >
            <span className="text-xs font-bold">1.</span>
          </MenuButton>

          <div className="w-px h-6 bg-gray-300 mx-1" />

          {/* Alignment */}
          <select
            value={
              editor.isActive({ textAlign: 'center' })
                ? 'center'
                : editor.isActive({ textAlign: 'right' })
                ? 'right'
                : 'left'
            }
            onChange={(e) => {
              editor.chain().focus().setTextAlign(e.target.value).run();
            }}
            className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-brand"
          >
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>

          <div className="w-px h-6 bg-gray-300 mx-1" />

          {/* Link & Image */}
          <MenuButton onClick={setLink} isActive={editor.isActive('link')} title="Add Link">
            <LinkIcon className="w-4 h-4" />
          </MenuButton>
          <MenuButton onClick={addImage} title="Add Image">
            <PhotoIcon className="w-4 h-4" />
          </MenuButton>

          <div className="w-px h-6 bg-gray-300 mx-1" />

          {/* Color picker */}
          <input
            type="color"
            onInput={(e) =>
              editor.chain().focus().setColor((e.target as HTMLInputElement).value).run()
            }
            value={editor.getAttributes('textStyle').color || '#000000'}
            className="w-8 h-8 rounded cursor-pointer"
            title="Text Color"
            aria-label="Text color"
          />

          <div className="flex-1" />

          {/* HTML Mode Toggle */}
          <MenuButton
            onClick={() => onHtmlModeChange?.(true)}
            title="View HTML Source"
          >
            <CodeBracketIcon className="w-4 h-4" />
          </MenuButton>
        </div>

        {/* Editor Content */}
        <EditorContent editor={editor} className="bg-white" />
      </div>
    );
  }
);

TipTapEditor.displayName = 'TipTapEditor';

export default TipTapEditor;
